import { isValidCode, normalizeCode } from "@brouhaha/codes";
import { t } from "@brouhaha/i18n";
import { BrouhahaMark, Button, Card } from "@brouhaha/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import type { FormEvent } from "react";

import { WORKBENCH_ENABLED } from "../lib/flags";

export const Route = createFileRoute("/")({
  component: Home,
});

/** Section 6.12 of the design doc, reduced to the one action that matters today. */
function Home() {
  const navigate = useNavigate();
  const inputId = useId();
  const hintId = useId();
  const [raw, setRaw] = useState("");
  const [invalid, setInvalid] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    const code = normalizeCode(raw);
    if (!isValidCode(code)) {
      setInvalid(true);
      return;
    }
    void navigate({ to: "/c/$code", params: { code } });
  }

  return (
    <main className="flex flex-1 flex-col gap-8">
      <header className="flex items-center gap-4">
        <BrouhahaMark size={56} />
        <div>
          <h1 className="text-xl font-bold">{t("app.title")}</h1>
          <p className="text-sm text-ink-muted">{t("app.tagline")}</p>
        </div>
      </header>

      <div className="flex-1" />

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Card>
          <label htmlFor={inputId} className="block font-semibold">
            {t("home.codeLabel")}
          </label>
          <p id={hintId} className="mt-1 text-sm text-ink-muted">
            {invalid ? t("home.codeInvalid") : t("home.codeHint")}
          </p>
          <input
            id={inputId}
            name="code"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={9}
            placeholder={t("home.codePlaceholder")}
            aria-describedby={hintId}
            aria-invalid={invalid || undefined}
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setInvalid(false);
            }}
            className="mt-4 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4 text-center font-mono text-lg tracking-[0.2em] text-ink uppercase placeholder:text-ink-muted/60 aria-invalid:border-rose"
          />
        </Card>
        <Button type="submit" full>
          {t("home.join")}
        </Button>
      </form>

      <p className="flex justify-center gap-6 text-center text-sm">
        <Link to="/kit" className="text-ink-muted underline underline-offset-4">
          {t("home.kitLink")}
        </Link>
        {WORKBENCH_ENABLED && (
          <Link to="/workbench" className="text-ink-muted underline underline-offset-4">
            {t("home.workbenchLink")}
          </Link>
        )}
      </p>
    </main>
  );
}
